import { getCountertopRunIsland } from './get-countertop-run-island';
import { getGroupCountertopCutoutWidth, isCountertopCutoutItem } from './get-group-countertop-edge';
import { GRANITE_SEAM_CATEGORIES, GRANITE_SEAM_SKUS, GRILL_CATEGORIES } from '../constants';

import type { Countertop } from '../../countertops/countertop.class';
import type { PanelItem } from '../../panel-items/panel-item.class';
import type { PanelItemGroup } from '../../panel-item-groups/panel-item-group.class';

/** A countertop cutout on the run, with the span a seam may land anywhere inside. */
export type CountertopSeamGroup = {
	group: PanelItemGroup;
	center: number;
	left: number;
	right: number;
	anchorItemId: string | null;
	anchorSku: string | null;
	isGrill: boolean;
	demandsGraniteSeam: boolean;
};

// Fuel appliances are one catalog row per fuel (apps/api/prisma/split-fuel-variants.js), so a
// placed appliance's sku is the variant while the documented rule names the family.
const seamSkuKey = (panelItem: PanelItem): string => panelItem.item.component?.fuelFamily ?? panelItem.item.sku ?? '';

const isGrillItem = (panelItem: PanelItem): boolean => GRILL_CATEGORIES.includes(panelItem.item.category?.description ?? '');

const demandsGraniteSeamItem = (panelItem: PanelItem): boolean => {
	return GRANITE_SEAM_CATEGORIES.includes(panelItem.item.category?.description ?? '')
		|| GRANITE_SEAM_SKUS.includes(seamSkuKey(panelItem));
};

const toSeamGroup = (group: PanelItemGroup): CountertopSeamGroup | null => {
	const cutoutWidth = getGroupCountertopCutoutWidth(group);
	if (!cutoutWidth) return null;

	// Geometry comes from the group (one cutout per column), classification from every item in it --
	// a MOK grill column also holds an insulated jacket, which is countertop-placed but no trigger.
	const topItems = group.items.children.filter((panelItem) => isCountertopCutoutItem(panelItem as PanelItem)) as Array<PanelItem>;
	const grill = topItems.find(isGrillItem) ?? null;
	const trigger = topItems.find(demandsGraniteSeamItem) ?? null;
	const anchor = trigger ?? grill ?? topItems[topItems.length - 1] ?? null;
	const halfCutoutWidth = cutoutWidth / 2;

	return {
		group,
		center: group.center.x,
		left: group.center.x - halfCutoutWidth,
		right: group.center.x + halfCutoutWidth,
		anchorItemId: anchor?.id ?? null,
		anchorSku: anchor?.item?.sku ?? null,
		isGrill: grill !== null,
		demandsGraniteSeam: trigger !== null
	};
};

/**
 * Every countertop cutout on a run, left to right -- the things a seam can be placed at.
 * @param countertop the countertop whose run is being divided
 * @returns the cutouts, classified for the placement ladder and the granite seam rule
 */
export const getCountertopSeamGroups = (countertop: Countertop): Array<CountertopSeamGroup> => {
	const island = getCountertopRunIsland(countertop);
	const frontPanel = island?.panels?.getFrontPanel() ?? null;
	if (!frontPanel) return [];

	return frontPanel.panelItemGroups.children
		.map((group) => toSeamGroup(group as PanelItemGroup))
		.filter((seamGroup): seamGroup is CountertopSeamGroup => seamGroup !== null)
		.sort((a, b) => a.center - b.center);
};
